import {ToolCallStatus, useHumanInTheLoop} from "@copilotkit/react-core/v2";
import {ReceiptIcon} from "@project/icons-web";
import {z} from "zod";

import {ToolCallSummary} from "../components/tool-call-summary";
import {ToolCallWrapper} from "../components/tool-call-wrapper";

/**
 * Registry for Human-in-the-Loop (HITL) tools.
 * Exposes approval and confirmation workflows that pause the agent until the user responds.
 */
export function HITLRegistry() {
  useHumanInTheLoop(
    {
      name: "approveExpense",
      description: "Ask the user to approve a pending expense",
      parameters: z.object({
        employeeName: z.string(),
        amount: z.number(),
        category: z.string(),
        description: z.string(),
      }),
      render: ({args, respond, status, result}) => {
        // useHumanInTheLoop doesn't expose result natively when we use `respond`.
        // Actually, we can just check if respond is undefined to know if it's resolved.
        const isResolved = status === ToolCallStatus.Complete;

        let resolvedValue: Record<string, unknown> | null = null;

        if (result) {
          try {
            resolvedValue =
              typeof result === "string"
                ? (JSON.parse(result) as Record<string, unknown>)
                : (result as Record<string, unknown>);
          } catch {
            // ignore JSON parse error
          }
        }

        return (
          <ToolCallWrapper
            icon={<ReceiptIcon className="size-4" />}
            name="Approve Expense"
            title={`${args.employeeName} - $${args.amount}`}
            status={status}
            approval={
              !isResolved && respond
                ? {
                    onApprove: () => respond({approved: true}),
                    onReject: () => respond({approved: false}),
                  }
                : undefined
            }
            resultLabel={
              isResolved ? (resolvedValue?.approved ? "Approved" : "Rejected") : undefined
            }
            severity={isResolved && !resolvedValue?.approved ? "danger" : undefined}
            defaultExpanded
          >
            <ToolCallSummary>
              <ToolCallSummary.Row label="Employee" value={args.employeeName} />
              <ToolCallSummary.Row
                label="Amount"
                value={<span className="font-mono">${args.amount}</span>}
              />
              <ToolCallSummary.Row label="Category" value={args.category} />
              <ToolCallSummary.Separator />
              <ToolCallSummary.Row
                label="Description"
                value={<span className="italic">"{args.description}"</span>}
              />
            </ToolCallSummary>
          </ToolCallWrapper>
        );
      },
    },
    [],
  );

  return null;
}
